import React from 'react'
import { Bar, HeatMap, ScatterPlot } from '@components'
import styled from '@emotion/styled'
import {
  HeatmapData,
  HistogramData,
  ScatterplotData,
  SwarmplotData,
} from '@types'
import { ResponsiveSwarmPlot } from '@nivo/swarmplot'
import { initTheme } from '@utils'
import { LegendProps } from '@nivo/legends'

interface Props {
  scatterplotData?: ScatterplotData
  heatmapData?: HeatmapData
  swarmplotData?: SwarmplotData
  barData1?: HistogramData
  barData2?: HistogramData
  attribute1Type: string
  attribute2Type: string
}

const customBarLegend: LegendProps[] = [
  {
    anchor: 'bottom',
    direction: 'row',
    justify: true,
    translateX: 170,
    translateY: 55,
    itemsSpacing: 2,
    itemWidth: 100,
    itemHeight: 20,
    itemDirection: 'left-to-right',
    itemOpacity: 0.85,
    symbolSize: 16,
    symbolShape: 'circle',
    effects: [
      {
        on: 'hover',
        style: {
          itemOpacity: 1,
        },
      },
    ],
  },
]

/**
 * Componente MoreInfo contente 3 grafici
 * @param scatterplotData {Array.<ScatterPlotRawSerie<ScatterPlotDatum>>} dati per il grafico Plotter
 * @param PDF {ITrendLine} dati per la PDF del Plotter
 * @param barData1 {HistogramData=} dati per il primo istogramma
 * @param barData2 {HistogramData=} dati per il secondo istogramma
 * @component
 */
export const MoreInfo: React.FC<Props> = ({
  scatterplotData,
  heatmapData,
  swarmplotData,
  barData1,
  barData2,
  attribute1Type,
  attribute2Type,
}) => {
  const floatFloat = () => {
    return (
      <>
        {scatterplotData && (
          <ScatterplotContainer>
            <ScatterPlot
              dataCy={'scatter-plot'}
              data={scatterplotData.data}
              showTrendLine={scatterplotData.PDF}
              customMargin={{ left: 70, top: 20, right: 40, bottom: 60 }}
              axisLegend={{ x: 'Data', y: 'Category' }}
              dimensions={{ width: '500px', height: '380px' }}
            />
          </ScatterplotContainer>
        )}
      </>
    )
  }

  const floatCategorical = () => {
    return (
      <>
        {swarmplotData && (
          <SwarmContainer>
            <ResponsiveSwarmPlot
              data={swarmplotData.data}
              groups={swarmplotData.groups}
              value={swarmplotData.value}
              valueScale={{
                type: 'linear',
                min: 'auto',
                max: 'auto',
                reverse: false,
              }}
              theme={initTheme('dark')}
              margin={{ top: 40, right: 50, bottom: 80, left: 80 }}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 90,
                legendPosition: 'middle',
                legendOffset: 46,
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legendPosition: 'middle',
                legendOffset: -60,
              }}
            />
          </SwarmContainer>
        )}
      </>
    )
  }

  const categoricalCategorical = () => {
    return (
      <>
        {heatmapData && (
          <ScatterplotContainer>
            <HeatMap
              dataCy={'correlation-chart'}
              data={heatmapData}
              dimensions={{ width: '100%', height: '380px' }}
              squaredBlock
              customMargin={{ top: 80, right: 0, bottom: 0, left: 80 }}
              customAxisTop={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: -89.9,
              }}
              customAxisBottom={null}
              customAxisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
              }}
              customAnimate={false}
              customLegend={[]}
              customInteractive={false}
              customColors={{
                type: 'diverging',
                scheme: 'yellow_green_blue',
                minValue: undefined,
                maxValue: undefined,
              }}
            />
          </ScatterplotContainer>
        )}
      </>
    )
  }

  const renderDistribution = () => {
    return (
      <DistributionContainer>
        <BarContainer>
          <Bar
            dataCy={'bar1'}
            {...barData1}
            bin={100}
            barPadding={attribute1Type === 'float' ? 0.0 : 0.3}
            margin={{ top: 10, bottom: 60, left: 10, right: 30 }}
            customLegend={customBarLegend}
            dimensions={{ width: '460px', height: '400px' }}
          />
        </BarContainer>

        <BarContainer>
          <Bar
            dataCy={'bar2'}
            {...barData2}
            bin={100}
            barPadding={attribute2Type === 'float' ? 0.0 : 0.3}
            margin={{ top: 10, bottom: 60, left: 10, right: 30 }}
            dimensions={{ width: '460px', height: '400px' }}
            customLegend={customBarLegend}
          />
        </BarContainer>
      </DistributionContainer>
    )
  }

  return (
    <ChartsContainer>
      <>
        {attribute1Type === 'float' &&
          attribute2Type === 'float' &&
          floatFloat()}
        {attribute1Type === 'float' &&
          attribute2Type === 'categorical' &&
          floatCategorical()}
        {attribute1Type === 'categorical' &&
          attribute2Type === 'float' &&
          floatCategorical()}
        {attribute1Type === 'categorical' &&
          attribute2Type === 'categorical' &&
          categoricalCategorical()}

        {renderDistribution()}
      </>
    </ChartsContainer>
  )
}

//region Style
const ChartsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  padding-left: 10px;
  max-width: 100%;
`
const ScatterplotContainer = styled.div`
  display: flex;
  flex: 1 1 40%;
  border: 1px solid white;
  border-radius: 10px;
  padding: 25px;
  align-items: center;
  justify-content: center;
`

const DistributionContainer = styled.div`
  display: flex;
  flex: 1 2;
  border: 1px solid white;
  border-radius: 10px;
  margin-left: 70px;
`

const BarContainer = styled.div`
  display: flex;
  flex: 1 1;
  padding: 25px;
  align-items: center;
  justify-content: center;
`

const SwarmContainer = styled.div`
  display: flex;
  flex: 1 1;
  width: 500px;
  height: 450px;
  border: 1px solid white;
  border-radius: 10px;
`
//endregion
